/* Create / edit a role: name, description, colour and the full permission
   matrix. System roles are shown read-only (view mode). */

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import s from "./userManagement.module.css";
import { IoArrowBack, IoShieldCheckmarkOutline } from "react-icons/io5";
import { getRole, createRole, updateRole } from "../../rbac/rbacService";
import { useRbac } from "../../rbac/RbacContext";
import { emptyPermissions } from "../../rbac/permissions";
import { showToast } from "../../utils/Toastify/Toastify";
import { Spinner } from "./uiBits";
import PermissionMatrix from "./PermissionMatrix";

const COLORS = ["#b87333", "#4f9d69", "#3b82c4", "#9b6dc6", "#c98a2b", "#c45b6d", "#5aa9a3"];

const RoleEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshRoles } = useRbac();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [systemRole, setSystemRole] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState(COLORS[0]);
  const [permissions, setPermissions] = useState(emptyPermissions());
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      setLoading(true);
      try {
        const role = await getRole(id);
        setName(role.name);
        setDescription(role.description || "");
        setColor(role.color || COLORS[0]);
        setPermissions(role.permissions);
        setSystemRole(Boolean(role.system));
      } catch {
        showToast("Role not found", "error");
        navigate("/mainlayout/roles");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const handleSave = async () => {
    const e = {};
    if (!name.trim()) e.name = "Role name is required";
    setErrors(e);
    if (Object.keys(e).length) return;

    setSaving(true);
    try {
      const payload = { name: name.trim(), description: description.trim(), color, permissions };
      if (isEdit) {
        await updateRole(id, payload);
        showToast("Role updated", "success");
      } else {
        await createRole(payload);
        showToast("Role created", "success");
      }
      refreshRoles();
      navigate("/mainlayout/roles");
    } catch (err) {
      showToast(err.message || "Failed to save role", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={s.page}>
        <Spinner label="Loading role…" />
      </div>
    );
  }

  return (
    <div className={s.page}>
      <button className={s.backLink} onClick={() => navigate("/mainlayout/roles")}>
        <IoArrowBack /> Back to roles
      </button>

      <div className={s.header}>
        <div className={s.headerTitleWrap}>
          <h1 className={s.title}>
            {systemRole ? "View role" : isEdit ? "Edit role" : "Create role"}
          </h1>
          <p className={s.subtitle}>
            {systemRole
              ? "System roles are read-only and cannot be modified."
              : "Name the role and choose exactly what it can access."}
          </p>
        </div>
        {!systemRole && (
          <div className={s.headerActions}>
            <button className={s.btnGhost} onClick={() => navigate("/mainlayout/roles")}>
              Cancel
            </button>
            <button className={s.btnPrimary} onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create role"}
            </button>
          </div>
        )}
      </div>

      <div className={s.detailCard} style={{ marginBottom: 18 }}>
        <div className={s.fieldRow}>
          <div className={s.field}>
            <label className={s.label}>
              Role name <span className={s.required}>*</span>
            </label>
            <input
              className={`${s.input} ${errors.name ? s.inputError : ""}`}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setErrors((er) => ({ ...er, name: undefined }));
              }}
              placeholder="e.g. Content Manager"
              disabled={systemRole}
              maxLength={40}
            />
            {errors.name && <span className={s.errorText}>{errors.name}</span>}
          </div>
          <div className={s.field}>
            <label className={s.label}>Colour</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center", height: 44 }}>
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => !systemRole && setColor(c)}
                  aria-label={`Colour ${c}`}
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 8,
                    background: c,
                    cursor: systemRole ? "default" : "pointer",
                    border:
                      color === c
                        ? "2px solid #fff"
                        : "2px solid transparent",
                    boxShadow:
                      color === c ? "0 0 0 2px rgba(255,255,255,0.25)" : "none",
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        <div className={s.field} style={{ marginTop: 14 }}>
          <label className={s.label}>Description</label>
          <textarea
            className={s.textarea}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this role for?"
            disabled={systemRole}
            maxLength={200}
          />
          {!systemRole && (
            <span className={s.hint} style={{ alignSelf: "flex-end" }}>
              {description.length}/200
            </span>
          )}
        </div>
      </div>

      <div className={s.detailCard}>
        <div className={s.sectionTitle}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <IoShieldCheckmarkOutline /> Permissions
          </span>
        </div>
        <p className={s.sectionDesc}>
          Tick the actions this role may perform in each module. Click a column
          header to toggle that action across all modules.
        </p>
        <PermissionMatrix
          value={permissions}
          onChange={setPermissions}
          readOnly={systemRole}
        />
      </div>
    </div>
  );
};

export default RoleEditor;
